import type {
  IRealtimeTransport,
  RealtimeListener,
  RealtimeMessage,
  RealtimeState,
} from './IRealtimeTransport';

export class WebSocketTransport implements IRealtimeTransport {
  private ws: WebSocket | null = null;
  private url = '';
  private token: string | undefined;
  private state: RealtimeState = 'idle';
  private listeners = new Map<string, Set<RealtimeListener>>();
  private stateListeners = new Set<(s: RealtimeState) => void>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = true;

  private setState(s: RealtimeState): void {
    this.state = s;
    this.stateListeners.forEach((cb) => cb(s));
  }

  isConnected(): boolean {
    return this.state === 'connected';
  }

  onStateChange(cb: (s: RealtimeState) => void): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }

  connect(url: string, token?: string): Promise<void> {
    this.url = url;
    this.token = token;
    this.shouldReconnect = true;
    return this.open();
  }

  private open(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.setState(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
        const url = this.token ? `${this.url}?token=${encodeURIComponent(this.token)}` : this.url;
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          this.setState('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data) as RealtimeMessage;
            const set = this.listeners.get(msg.topic);
            if (set) set.forEach((cb) => cb(msg));
          } catch {
            /* ignore */
          }
        };

        this.ws.onerror = () => {
          if (this.state === 'connecting') reject(new Error('WebSocket connection failed'));
        };

        this.ws.onclose = () => {
          this.setState('closed');
          this.ws = null;
          if (this.shouldReconnect) this.scheduleReconnect();
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = Math.min(30_000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.open().catch(() => this.scheduleReconnect());
    }, delay);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.setState('idle');
  }

  subscribe<T>(topic: string, listener: RealtimeListener<T>): () => void {
    if (!this.listeners.has(topic)) this.listeners.set(topic, new Set());
    this.listeners.get(topic)!.add(listener as RealtimeListener);
    return () => this.listeners.get(topic)?.delete(listener as RealtimeListener);
  }

  publish(topic: string, payload: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ topic, payload, timestamp: Date.now() }));
    }
  }
}
